/**
 * PostHog Analytics - Cross-platform event tracking
 * See docs/POSTHOG_ANALYTICS_ARCHITECTURE.md for event taxonomy
 */

import { setHttpErrorSink } from './requestId';

let posthog = null;

export const initAnalytics = () => {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
  const debug = process.env.REACT_APP_POSTHOG_DEBUG === 'true';

  if (!key) {
    if (debug || process.env.NODE_ENV === 'development') {
      console.warn('[PostHog] REACT_APP_POSTHOG_KEY not set - analytics disabled. Add it to .env and restart.');
    }
    return;
  }
  try {
    posthog = require('posthog-js').default;
    posthog.init(key, {
      api_host: host,
      person_profiles: 'identified_only',
      capture_pageview: true,
      debug: debug,
    });
    posthog.register({
      platform: 'web',
      platform_version: '1.0',
    });
    if (debug || process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[PostHog] Initialized', { host, keyPrefix: key.substring(0, 12) + '...' });
    }
    setHttpErrorSink((props) => trackHttp5xx(props));
  } catch (e) {
    console.warn('PostHog init failed:', e);
  }
};

const capture = (event, properties = {}) => {
  if (!posthog) return;
  try {
    posthog.capture(event, { ...properties, platform: 'web' });
    if (process.env.REACT_APP_POSTHOG_DEBUG === 'true') {
      // eslint-disable-next-line no-console
      console.log('[PostHog] capture:', event, properties);
    }
  } catch (e) {
    console.warn('Analytics capture failed:', e);
  }
};

/** First touch - session start (call on app load / HomePage) */
export const trackVisitorLanded = (props = {}) => {
  const params = new URLSearchParams(window.location.search);
  capture('visitor_landed', {
    referrer: document.referrer || undefined,
    landing_page: window.location.pathname || '/',
    is_first_visit: !sessionStorage.getItem('flea_visited'),
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
    ...props,
  });
  sessionStorage.setItem('flea_visited', '1');
};

/** OTP request failed — no phone numbers or other PII. */
export const trackOtpSendFailed = (props = {}) => {
  capture('otp_send_failed', {
    purpose: props.purpose,
    attempt: props.attempt,
    error_code: props.error_code,
    http_status: props.http_status ?? null,
    will_retry: Boolean(props.will_retry),
  });
};

/** OTP request succeeded — no phone numbers or other PII. */
export const trackOtpSendSucceeded = (props = {}) => {
  capture('otp_send_succeeded', {
    purpose: props.purpose,
    attempt: props.attempt,
    delivery_channel: props.delivery_channel ?? null,
    fallback_used: Boolean(props.fallback_used),
  });
};

/** User initiated signup */
export const trackSignupStarted = (props = {}) => {
  capture('signup_started', {
    signup_method: 'phone',
    entry_point: 'register_page',
    ...props,
  });
};

/** User successfully registered - call identify() after */
export const trackSignupCompleted = (userId, props = {}) => {
  capture('signup_completed', {
    signup_method: 'phone',
    user_id: String(userId),
    ...props,
  });
};

/** User successfully logged in - call identify() after */
export const trackLoginCompleted = (userId, props = {}) => {
  capture('login_completed', {
    user_id: String(userId),
    ...props,
  });
};

/** Identify user (links anonymous → identified) */
export const identifyUser = (userId, traits = {}) => {
  if (!posthog || !userId) return;
  try {
    posthog.identify(String(userId));
    posthog.alias(String(userId));
    if (Object.keys(traits).length) {
      posthog.people.set(traits);
    }
  } catch (e) {
    console.warn('Analytics identify failed:', e);
  }
};

/** Item detail viewed */
export const trackItemViewed = (itemId, props = {}) => {
  capture('item_viewed', {
    item_id: String(itemId),
    ...props,
  });
};

/** Item added to cart */
export const trackItemAddedToCart = (itemId, props = {}) => {
  capture('item_added_to_cart', {
    item_id: String(itemId),
    ...props,
  });
};

/** User began checkout */
export const trackCheckoutStarted = (props = {}) => {
  capture('checkout_started', props);
};

/** Order successfully placed (GMV source) */
export const trackOrderCompleted = (order) => {
  const totalAmount = parseFloat(order?.total_amount ?? order?.total ?? 0);
  const orderId = order?.id ?? order?.order_id ?? order?.orderId;
  capture('order_completed', {
    order_id: String(orderId),
    total_amount: totalAmount,
    currency: 'points',
    item_count: order?.items?.length ?? order?.item_count ?? 0,
    payment_method: 'points',
    is_first_purchase: order?.is_first_purchase ?? undefined,
    user_id: order?.user_id ? String(order.user_id) : undefined,
  });
};

/** Designer item viewed */
export const trackDesignerItemViewed = (itemId, props = {}) => {
  capture('designer_item_viewed', { item_id: String(itemId), ...props });
};

/** Designer item added to cart */
export const trackDesignerItemAddedToCart = (designerItemId, props = {}) => {
  capture('designer_item_added_to_cart', { designer_item_id: String(designerItemId), ...props });
};

/** Designer item submitted */
export const trackDesignerItemOffered = (props = {}) => {
  capture('designer_item_offered', props);
};

/** Boutique item viewed */
export const trackBoutiqueItemViewed = (itemId, props = {}) => {
  capture('boutique_item_viewed', { item_id: String(itemId), ...props });
};

/** Boutique item added to cart */
export const trackBoutiqueItemAddedToCart = (boutiqueItemId, props = {}) => {
  capture('boutique_item_added_to_cart', { boutique_item_id: String(boutiqueItemId), ...props });
};

/** Boutique item submitted */
export const trackBoutiqueItemOffered = (props = {}) => {
  capture('boutique_item_offered', props);
};

/** Item clicked (navigating from list to detail) */
export const trackItemClicked = (itemId, props = {}) => {
  capture('item_clicked', {
    item_id: String(itemId),
    ...props,
  });
};

/** Item added to favorites (not fired on remove) */
export const trackItemFavorited = (itemId, props = {}) => {
  capture('item_favorited', {
    item_id: String(itemId),
    ...props,
  });
};

/**
 * Item sold — fire once per line item when an order completes.
 * Captures seller activity so PostHog can compute selling rate per month.
 */
export const trackItemSold = (props = {}) => {
  capture('item_sold', props);
};

/** Item flagged by a user */
export const trackItemFlagged = (listingId, listingType, reason) => {
  capture('item_flagged', {
    listing_id: String(listingId),
    listing_type: listingType,
    reason,
  });
};

/** Marketplace listing create funnel */
export const trackListingCreateStarted = (props = {}) => {
  capture('listing_create_started', props);
};

export const trackListingCreateSucceeded = ({ item_id, category, listing_type, ...rest } = {}) => {
  capture('listing_create_succeeded', {
    ...rest,
    item_id: item_id != null ? String(item_id) : undefined,
    category,
    listing_type,
  });
};

export const trackListingCreateFailed = ({ error, status, category, listing_type, ...rest } = {}) => {
  capture('listing_create_failed', {
    ...rest,
    error: error != null ? String(error).slice(0, 500) : undefined,
    status: status != null ? String(status) : undefined,
    category,
    listing_type,
  });
};

/** Shop / gallery create funnel — artist, designer, boutique, local_store, wedding */
export const trackShopCreateStarted = (props = {}) => {
  capture('shop_create_started', props);
};

export const trackShopCreateSucceeded = ({ shop_id, shop_type, ...rest } = {}) => {
  capture('shop_create_succeeded', {
    ...rest,
    shop_id: shop_id != null ? String(shop_id) : undefined,
    shop_type,
  });
};

export const trackShopCreateFailed = ({ error, status, shop_type, ...rest } = {}) => {
  capture('shop_create_failed', {
    ...rest,
    error: error != null ? String(error).slice(0, 500) : undefined,
    status: status != null ? String(status) : undefined,
    shop_type,
  });
};

export const trackHttp5xx = ({ status, path, method, request_id, duration_ms, ...rest } = {}) => {
  capture('http_5xx', {
    ...rest,
    status: status != null ? String(status) : undefined,
    path,
    method,
    request_id,
    duration_ms: duration_ms != null ? Number(duration_ms) : undefined,
  });
};

const PAINTING_PROPERTY_KEYS = new Set([
  'entry_point', 'request_id', 'shop_id', 'catalog_item_id', 'negotiation_id',
  'quote_id', 'order_id', 'width_cm', 'height_cm', 'creative_direction',
  'color_preference', 'reference_count', 'uploaded_count', 'artist_count',
  'fallback', 'budget_min_cents', 'budget_max_cents', 'amount_cents',
  'delivery_days', 'is_revision', 'state',
]);

const PAINTING_EVENTS = new Set([
  'painting_customize_clicked',
  'painting_request_started',
  'painting_request_completed',
  'painting_reference_uploaded',
  'painting_artists_viewed',
  'painting_artist_selected',
  'painting_request_sent',
  'painting_quote_sent',
  'painting_quote_accepted',
  'painting_quote_declined',
  'painting_request_converted_to_order',
]);

/** Allowlist painting funnel properties so user-authored brief content cannot leak. */
export const trackPaintingCustomization = (event, props = {}) => {
  if (!PAINTING_EVENTS.has(event)) return;
  const safe = Object.fromEntries(
    Object.entries(props).filter(([key]) => PAINTING_PROPERTY_KEYS.has(key)),
  );
  capture(event, safe);
};

const analytics = { initAnalytics, capture, identifyUser };
export default analytics;
